import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AudioButton } from '../components/AudioButton';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Clock, BookOpen, Headphones, PenLine, Mic,
  Play, Award, Loader2, CheckCircle, XCircle, Timer, Download,
  GraduationCap, FileText, Volume2, ArrowRight, RotateCcw, Trophy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_ICONS = { lesen: BookOpen, hoeren: Headphones, schreiben: PenLine, sprechen: Mic };
const SECTION_COLORS = { lesen: '#3b82f6', hoeren: '#8b5cf6', schreiben: '#f59e0b', sprechen: '#14b8a6' };
const PROVIDER_STYLES = {
  goethe: { color: '#22c55e', bg: 'from-green-500/10 to-emerald-500/5', border: 'border-green-500/30', label: 'Goethe-Institut' },
  telc: { color: '#3b82f6', bg: 'from-blue-500/10 to-sky-500/5', border: 'border-blue-500/30', label: 'telc' },
};

export default function ExamSimulation() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, histRes, certRes] = await Promise.all([
        axios.get(`${API}/exams/catalog`),
        axios.get(`${API}/exams/history`).catch(() => ({ data: { history: [] } })),
        axios.get(`${API}/exams/certificates`).catch(() => ({ data: { certificates: [] } })),
      ]);
      setCatalog(catRes.data.exams || []);
      setHistory(histRes.data.history || []);
      setCertificates(certRes.data.certificates || []);
    } catch (err) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const startExam = async (examId, mode) => {
    try {
      const res = await axios.get(`${API}/exams/${examId}/start?mode=${mode}`);
      setSession(res.data);
      setResult(null);
    } catch (err) {
      toast.error('Failed to start exam');
    }
  };

  const handleExamComplete = (res) => {
    setResult(res);
    setSession(null);
    fetchData();
  };

  if (session) {
    return <ExamRunner session={session} onComplete={handleExamComplete} onExit={() => setSession(null)} />;
  }

  if (result) {
    return <ExamResults result={result} onBack={() => setResult(null)} />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="exam-simulation-page">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="exam-back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Prüfungssimulation</h1>
            <p className="text-sm text-muted-foreground">Goethe & telc Exam Simulation</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">{[...Array(4)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-48 rounded-xl" />)}</div>
        ) : (
          <>
            {/* Certificates Banner */}
            {certificates.length > 0 && (
              <Card className="mb-8 border-t-4 border-t-amber-500 bg-gradient-to-r from-amber-500/5 to-transparent" data-testid="certificates-banner">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-3 mb-3">
                    <Trophy className="h-6 w-6 text-amber-500" />
                    <h2 className="font-heading text-lg font-semibold">Ihre Zertifikate / Your Certificates</h2>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {certificates.map(cert => (
                      <div key={cert.exam_id} className="flex items-center gap-2 bg-amber-500/10 rounded-lg px-3 py-2 border border-amber-500/20">
                        <Award className="h-4 w-4 text-amber-500" />
                        <span className="text-sm font-medium">{cert.level} {cert.provider}</span>
                        <Badge className="bg-green-500 text-xs">{cert.percentage}%</Badge>
                        <a href={`${API}/exams/certificates/${cert.exam_id}/pdf`} target="_blank" rel="noopener noreferrer" className="ml-1">
                          <Download className="h-3.5 w-3.5 text-amber-500 hover:text-amber-400" />
                        </a>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Exam Catalog by Provider */}
            {['goethe', 'telc'].map(provider => {
              const style = PROVIDER_STYLES[provider];
              const providerExams = catalog.filter(e => e.provider === provider);
              return (
                <div key={provider} className="mb-10">
                  <div className="flex items-center gap-3 mb-4">
                    <GraduationCap className="h-6 w-6" style={{ color: style.color }} />
                    <h2 className="font-heading text-xl font-bold">{style.label}</h2>
                    <Badge style={{ background: style.color }}>{providerExams.length} Prüfungen</Badge>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {providerExams.map(exam => {
                      const cert = certificates.find(c => c.exam_id === exam.exam_id);
                      const lastAttempt = history.find(h => h.exam_id === exam.exam_id);
                      return (
                        <Card key={exam.exam_id} className={`bg-gradient-to-br ${style.bg} ${style.border} hover:shadow-lg transition-all`} data-testid={`exam-card-${exam.exam_id}`}>
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle className="text-lg">{exam.title}</CardTitle>
                                <p className="text-xs text-muted-foreground mt-1">{exam.subtitle}</p>
                              </div>
                              <Badge style={{ background: style.color }}>{exam.level}</Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {exam.sections.map(sec => {
                                const Icon = SECTION_ICONS[sec.section_id] || FileText;
                                return (
                                  <div key={sec.section_id} className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-background/50 border border-border/50">
                                    <Icon className="h-3 w-3" style={{ color: SECTION_COLORS[sec.section_id] }} />
                                    <span>{sec.title.split(' ')[0]}</span>
                                    <span className="text-muted-foreground">({sec.questions})</span>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{exam.full_duration_minutes} min (full)</span>
                              <span className="flex items-center gap-1"><Timer className="h-3 w-3" />{exam.quick_duration_minutes} min (quick)</span>
                              <span>{exam.total_questions} Fragen</span>
                            </div>
                            {cert && <div className="flex items-center gap-2 mb-3 text-xs"><CheckCircle className="h-4 w-4 text-green-500" /><span className="text-green-600 font-medium">Bestanden: {cert.percentage}% — {cert.grade}</span></div>}
                            {lastAttempt && !cert && <div className="flex items-center gap-2 mb-3 text-xs"><XCircle className="h-4 w-4 text-red-500" /><span className="text-red-600">Letzter Versuch: {lastAttempt.result?.percentage}%</span></div>}
                            <div className="flex gap-2">
                              <Button size="sm" className="flex-1" style={{ background: style.color }} onClick={() => startExam(exam.exam_id, 'full')} data-testid={`start-full-${exam.exam_id}`}>
                                <Play className="h-4 w-4 mr-1" /> Vollständige Prüfung
                              </Button>
                              <Button size="sm" variant="outline" className="flex-1" onClick={() => startExam(exam.exam_id, 'quick')} data-testid={`start-quick-${exam.exam_id}`}>
                                <Timer className="h-4 w-4 mr-1" /> Schnelltest
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* History */}
            {history.length > 0 && (
              <Card className="mt-6" data-testid="exam-history">
                <CardHeader><CardTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5" />Letzte Versuche / Recent Attempts</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {history.slice(0, 10).map((h, i) => (
                      <div key={`hist-${h.session_id}-${i}`} className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <p className="font-medium text-sm">{h.result?.title || h.exam_id}</p>
                          <p className="text-xs text-muted-foreground">{h.result?.mode === 'quick' ? 'Schnelltest' : 'Vollständig'} — {h.completed_at?.slice(0, 10)}</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-lg font-bold ${(h.result?.percentage || 0) >= 60 ? 'text-green-500' : 'text-red-500'}`}>{h.result?.percentage}%</span>
                          <p className="text-xs text-muted-foreground">{h.result?.grade}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}

/* ==================== EXAM RUNNER ==================== */
function ExamRunner({ session, onComplete, onExit }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [writingTexts, setWritingTexts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sectionTimeLeft, setSectionTimeLeft] = useState(0);
  const timerRef = useRef(null);

  const sections = session.sections || [];
  const sec = sections[currentSection];
  const questions = sec?.questions || [];
  const q = questions[currentQuestion];
  const totalQs = sections.reduce((sum, s) => sum + s.questions.length, 0);
  const answeredCount = Object.keys(answers).length;
  const providerStyle = PROVIDER_STYLES[session.provider] || PROVIDER_STYLES.goethe;

  useEffect(() => {
    if (sec) setSectionTimeLeft(sec.time_minutes * 60);
  }, [currentSection, sec]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSectionTimeLeft(prev => {
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const submitAnswer = async (qId, answer, score = 0, transcribed = '') => {
    setAnswers(prev => ({ ...prev, [qId]: { answer, score } }));
    try {
      await axios.post(`${API}/exams/${session.session_id}/answer`, null, {
        params: { q_id: qId, answer: String(answer), score, transcribed_text: transcribed }
      });
    } catch (err) {
      toast.error('Failed to submit answer');
    }
  };

  const submitWriting = async (qId) => {
    const text = writingTexts[qId] || '';
    if (!text.trim()) { toast.error('Please write something first'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/exams/${session.session_id}/grade-writing`, null, {
        params: { q_id: qId, text }
      });
      setAnswers(prev => ({ ...prev, [qId]: { answer: text, score: res.data.score, grading: res.data.grading } }));
      toast.success(`Score: ${res.data.score}%`);
    } catch (err) {
      toast.error('Grading failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSpeaking = (qId, transcribed, pronScore) => {
    const score = pronScore?.overall_score || 0;
    submitAnswer(qId, transcribed, score, transcribed);
  };

  const goNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else if (currentSection < sections.length - 1) {
      setCurrentSection(prev => prev + 1);
      setCurrentQuestion(0);
    }
  };

  const goPrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    } else if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      setCurrentQuestion(sections[currentSection - 1].questions.length - 1);
    }
  };

  const finishExam = async () => {
    if (!window.confirm('Prüfung abgeben? / Submit exam?')) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/exams/${session.session_id}/complete`);
      onComplete(res.data);
    } catch (err) {
      toast.error('Failed to complete exam');
    } finally {
      setSubmitting(false);
    }
  };

  const isLast = currentSection === sections.length - 1 && currentQuestion === questions.length - 1;
  const SectionIcon = SECTION_ICONS[sec?.section_id] || FileText;
  const sectionColor = SECTION_COLORS[sec?.section_id] || '#94a3b8';

  return (
    <div className="min-h-screen bg-background" data-testid="exam-runner">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onExit} data-testid="exam-exit"><ChevronLeft className="h-4 w-4" /> Exit</Button>
            <Badge style={{ background: providerStyle.color }}>{session.title}</Badge>
            <Badge variant="outline">{session.mode === 'quick' ? 'Schnelltest' : 'Vollständig'}</Badge>
          </div>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full font-mono text-sm font-bold ${sectionTimeLeft < 60 ? 'bg-red-500/20 text-red-500' : 'bg-muted text-foreground'}`}>
              <Clock className="h-4 w-4" /> {formatTime(sectionTimeLeft)}
            </div>
            <span className="text-sm text-muted-foreground">{answeredCount}/{totalQs}</span>
          </div>
        </div>
      </div>

      {/* Section tabs */}
      <div className="bg-muted/30 border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-2 flex gap-2 overflow-x-auto">
          {sections.map((s, i) => {
            const Icon = SECTION_ICONS[s.section_id] || FileText;
            const color = SECTION_COLORS[s.section_id];
            const sectionAnswered = s.questions.filter(q2 => answers[q2.q_id]).length;
            return (
              <button key={s.section_id} onClick={() => { setCurrentSection(i); setCurrentQuestion(0); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${i === currentSection ? 'bg-background shadow-sm border' : 'text-muted-foreground hover:text-foreground'}`}
                style={i === currentSection ? { borderColor: color } : {}}
                data-testid={`section-tab-${s.section_id}`}>
                <Icon className="h-4 w-4" style={{ color }} />
                {s.title_de || s.title}
                <span className="text-xs text-muted-foreground">({sectionAnswered}/{s.questions.length})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Question area */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Progress value={(answeredCount / totalQs) * 100} className="h-1.5 mb-6" />

        {q && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <SectionIcon className="h-6 w-6" style={{ color: sectionColor }} />
              <h2 className="text-lg font-semibold">{sec.title_de || sec.title}</h2>
              <span className="text-sm text-muted-foreground">Frage {currentQuestion + 1}/{questions.length}</span>
            </div>

            {/* LESEN */}
            {q.type === 'lesen' && (
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                    <p className="text-sm leading-relaxed">{q.text}</p>
                  </div>
                  <p className="font-semibold">{q.question}</p>
                  <div className="grid gap-2">
                    {q.options.map((opt, idx) => (
                      <button key={`opt-${q.q_id}-${idx}`} onClick={() => submitAnswer(q.q_id, idx, idx === answers[q.q_id]?.answer ? 100 : 0)}
                        className={`text-left p-3 rounded-lg border transition-all ${answers[q.q_id]?.answer === idx ? 'border-blue-500 bg-blue-500/10 ring-2 ring-blue-500/30' : 'border-border hover:border-blue-500/50 hover:bg-accent/30'}`}
                        data-testid={`option-${q.q_id}-${idx}`}>
                        <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span> {opt}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* HÖREN */}
            {q.type === 'hoeren' && (
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4 flex items-center gap-4">
                    <AudioButton text={q.audio_text} voice="nova" size="default" />
                    <div>
                      <p className="font-medium text-purple-600">Hörtext anhören</p>
                      <p className="text-xs text-muted-foreground">Listen to the audio, then answer the question</p>
                    </div>
                  </div>
                  <p className="font-semibold">{q.question}</p>
                  <div className="grid gap-2">
                    {q.options.map((opt, idx) => (
                      <button key={`opt-${q.q_id}-${idx}`} onClick={() => submitAnswer(q.q_id, idx)}
                        className={`text-left p-3 rounded-lg border transition-all ${answers[q.q_id]?.answer === idx ? 'border-purple-500 bg-purple-500/10 ring-2 ring-purple-500/30' : 'border-border hover:border-purple-500/50 hover:bg-accent/30'}`}
                        data-testid={`option-${q.q_id}-${idx}`}>
                        <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span> {opt}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* SCHREIBEN */}
            {q.type === 'schreiben' && (
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                    <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">{q.prompt_de}</p>
                    <p className="text-sm text-muted-foreground">{q.prompt_en}</p>
                    <p className="text-xs text-muted-foreground mt-2">{q.min_words}–{q.max_words} Wörter / words</p>
                  </div>
                  <Textarea value={writingTexts[q.q_id] || ''} onChange={(e) => setWritingTexts(prev => ({ ...prev, [q.q_id]: e.target.value }))}
                    placeholder="Schreiben Sie hier... / Write here..." rows={8} className="resize-none" data-testid={`writing-${q.q_id}`} />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{(writingTexts[q.q_id] || '').split(/\s+/).filter(Boolean).length} Wörter</span>
                    {answers[q.q_id] ? (
                      <Badge className="bg-green-500">Bewertet: {answers[q.q_id].score}%</Badge>
                    ) : (
                      <Button onClick={() => submitWriting(q.q_id)} disabled={submitting} style={{ background: '#f59e0b' }} data-testid={`submit-writing-${q.q_id}`}>
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenLine className="h-4 w-4 mr-2" />}
                        KI-Bewertung / AI Grade
                      </Button>
                    )}
                  </div>
                  {answers[q.q_id]?.grading && (
                    <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
                      <p>{answers[q.q_id].grading.feedback_de}</p>
                      <p className="text-muted-foreground">{answers[q.q_id].grading.feedback_en}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* SPRECHEN */}
            {q.type === 'sprechen' && (
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="bg-teal-500/5 border border-teal-500/20 rounded-lg p-4">
                    <p className="font-semibold text-teal-700 dark:text-teal-400 mb-1">{q.task_de}</p>
                    <p className="text-sm text-muted-foreground">{q.task_en}</p>
                    {q.expected_phrases?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {q.expected_phrases.map((ph, i) => (
                          <span key={`ph-${ph.slice(0,6)}-${i}`} className="text-xs bg-teal-500/10 text-teal-600 px-2 py-0.5 rounded-full border border-teal-500/20">
                            {ph}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-4 py-4">
                    <MicrophoneButton
                      size="lg"
                      expectedText={q.expected_phrases?.join(' ') || q.task_de}
                      onTranscription={(text) => handleSpeaking(q.q_id, text, null)}
                      onPronunciationScore={(score) => {
                        handleSpeaking(q.q_id, '', score);
                        toast.success(`Aussprache: ${score.overall_score}%`);
                      }}
                    />
                    <p className="text-sm text-muted-foreground">Drücken und sprechen / Press and speak</p>
                    {answers[q.q_id] && (
                      <Badge className={answers[q.q_id].score >= 60 ? 'bg-green-500' : 'bg-amber-500'}>
                        Score: {answers[q.q_id].score}%
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4">
              <Button variant="outline" onClick={goPrev} disabled={currentSection === 0 && currentQuestion === 0} data-testid="prev-question">
                <ChevronLeft className="h-4 w-4 mr-1" /> Zurück
              </Button>
              <div className="flex gap-2">
                {isLast ? (
                  <Button onClick={finishExam} disabled={submitting} className="bg-green-600 hover:bg-green-700" data-testid="finish-exam">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Prüfung abgeben
                  </Button>
                ) : (
                  <Button onClick={goNext} data-testid="next-question">
                    Weiter <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== EXAM RESULTS ==================== */
function ExamResults({ result, onBack }) {
  const providerStyle = PROVIDER_STYLES[result.provider] || PROVIDER_STYLES.goethe;
  return (
    <div className="min-h-screen bg-background" data-testid="exam-results">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Card className={`text-center p-8 border-t-4`} style={{ borderTopColor: result.passed ? '#22c55e' : '#ef4444' }}>
          <div className="text-6xl mb-4">{result.passed ? '🎉' : '📝'}</div>
          <h1 className="font-heading text-3xl font-bold mb-2">{result.passed ? 'Bestanden! / Passed!' : 'Nicht bestanden / Not Passed'}</h1>
          <p className="text-muted-foreground mb-6">{result.title} — {result.mode === 'quick' ? 'Schnelltest' : 'Vollständige Prüfung'}</p>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="p-4 rounded-xl bg-muted/50">
              <p className={`text-4xl font-bold ${result.passed ? 'text-green-500' : 'text-red-500'}`}>{result.percentage}%</p>
              <p className="text-sm text-muted-foreground">Ergebnis</p>
            </div>
            <div className="p-4 rounded-xl bg-muted/50">
              <p className="text-4xl font-bold">{result.grade}</p>
              <p className="text-sm text-muted-foreground">Note</p>
            </div>
            <div className="p-4 rounded-xl bg-muted/50">
              <p className="text-4xl font-bold text-amber-500">+{result.xp_earned}</p>
              <p className="text-sm text-muted-foreground">XP</p>
            </div>
          </div>

          <h3 className="font-heading text-lg font-semibold mb-4 text-left">Ergebnisse nach Abschnitt / Results by Section</h3>
          <div className="space-y-3 mb-8">
            {result.section_results?.map(sec => {
              const Icon = SECTION_ICONS[sec.section_id] || FileText;
              const color = SECTION_COLORS[sec.section_id];
              return (
                <div key={sec.section_id} className="flex items-center gap-3 p-3 rounded-lg border">
                  <Icon className="h-5 w-5" style={{ color }} />
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">{sec.title}</p>
                    <Progress value={sec.percentage} className="h-1.5 mt-1" />
                  </div>
                  <span className={`text-lg font-bold ${sec.passed ? 'text-green-500' : 'text-red-500'}`}>{sec.percentage}%</span>
                  {sec.passed ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
                </div>
              );
            })}
          </div>

          <div className="flex gap-3 justify-center">
            {result.certificate_awarded && (
              <a href={`${API}/exams/certificates/${result.exam_id}/pdf`} target="_blank" rel="noopener noreferrer">
                <Button className="bg-amber-500 hover:bg-amber-600" data-testid="download-cert">
                  <Download className="h-4 w-4 mr-2" /> Zertifikat herunterladen
                </Button>
              </a>
            )}
            <Button variant="outline" onClick={onBack} data-testid="back-to-catalog">
              <ArrowRight className="h-4 w-4 mr-2" /> Zurück zur Übersicht
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
